/**
 * Phase 107 (Plan 107-09 / D-10) — delete-configuration confirmation.
 *
 * Mirrors `RevokeConfirmModal.tsx`'s title/body/footer structure via the
 * shared `Modal` shell, with no re-typed-confirmation-word input required
 * before the danger button activates — the same precedent
 * `DeleteBandConfirmModal.tsx` (Plan 107-08) already set: deleting a saved
 * configuration is lower-stakes and far more frequent than an offboarding
 * access revoke. Names the specific configuration being destroyed, per
 * 107-UI-SPEC.md's Copywriting Contract rule that destructive confirmations
 * always name the thing, never a generic "this item".
 */
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';

export interface DeleteConfigConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  configurationName: string;
  /**
   * MKS-346 — how many leads link this configuration. Deleting it unlinks
   * them (the join rows cascade; the leads themselves are untouched), so the
   * confirmation says so before anyone clicks rather than after.
   */
  leadCount?: number;
  isSubmitting?: boolean;
}

export function DeleteConfigConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  configurationName,
  leadCount = 0,
  isSubmitting = false,
}: DeleteConfigConfirmModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Delete "${configurationName}"?`}
      ariaLabelledBy="delete-config-modal-title"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={isSubmitting}>
            Delete
          </Button>
        </div>
      }
    >
      <p className="text-gray-600">This configuration and its price bands are gone for good.</p>
      {leadCount > 0 && (
        <p className="mt-2 text-gray-600">
          {leadCount === 1 ? '1 lead links' : `${leadCount} leads link`} to it and will be
          unlinked. {leadCount === 1 ? 'The lead itself stays' : 'The leads themselves stay'} exactly
          as {leadCount === 1 ? 'it is' : 'they are'}.
        </p>
      )}
    </Modal>
  );
}

export default DeleteConfigConfirmModal;
